<template>
  <div>
    <template>
      <div
        class="d-flex justify-content-between align-items-center"
        style="width: 100%; margin-bottom: 20px"
      >
        <h5 class="mb-0">
          {{ $t('documentRequests.title.documentList') }}
        </h5>
        <b-button
          v-if="submitDocumentsShown"
          v-b-modal.submit-modal
          variant="outline-primary"
          :disabled="selectedDocuments.length === 0"
        >
          {{ $t('documentRequests.btn.submitDocuments') }}
        </b-button>
      </div>
    </template>
    <div
      v-if="documents.length"
      style="height: 100%"
    >
      <b-card
        v-for="(document, index) in documents"
        :key="index"
        no-body
        class="mb-1"
      >
        <b-card-header
          header-tag="header"
          class="p-1"
          role="tab"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="lead collapse-title">{{ document.name }}</span>
          </div>
          <div
            v-if="document.canEdit"
            class="d-flex align-items-center"
          >
            <b-form-checkbox
              v-model="document.submitted"
              class="mr-1"
              @change="changeSubmittedDocument(document)"
            />
            <b-button
              v-b-modal.remove-document-modal
              v-ripple.400="'rgba(234, 84, 85, 0.15)'"
              size="sm"
              variant="outline-danger"
              class="mr-2"
              @click="indexDocumentRemoveItem = index"
            >
              <feather-icon
                icon="XIcon"
                size="10"
              />
            </b-button>
          </div>
          <p
            v-else
            class="mb-0 mr-2"
            style="font-weight: 500; color: #b9b9c3"
          >
            {{ $t('documentRequests.warningText.confirmed') }}
          </p>
        </b-card-header>
        <div>
          <ul
            style="width: 100%"
          >
            <li
              v-for="(attachment, attachmentIndex) in document.attachments"
              :key="`attachmentIndex-${attachmentIndex}`"
              style="margin-top: 20px"
            >
              <b-link
                variant="primary"
                :href="attachment.link"
                target="_blank"
              >
                <b>{{ attachment.name }}</b>
              </b-link>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <b-modal
      id="remove-document-modal"
      :title="$t('application.deleteDocumentModal.title')"
      ok-variant="danger"
      @ok="removeDocument"
    >
      <p class="mt-1 d-flex justify-content-between">
        <span>
          {{ $t("application.deleteDocumentModal.content") }}
        </span>
      </p>
    </b-modal>

    <b-modal
      id="submit-modal"
      title="Submit Documents"
    >
      <validation-observer ref="rules">
        <b-form
          ref="form"
          @submit.stop.prevent="handleSubmit"
        >
          <b-form-group
            v-if="lääkehoitolupaExist"
            :label="$t('application.documentList.loveGranted.label')"
            label-for="lääkehoitolupaDocument"
          >
            <validation-provider
              #default="{ errors }"
              name="Love granted date"
              rules="required"
            >
              <flat-pickr
                v-model="loveGranted"
                class="form-control"
                :config="{ time_24hr: true }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            v-if="diplomaExist"
            :label="$t('application.documentList.graduation.label')"
            label-for="diplomaDocument"
          >
            <validation-provider
              #default="{ errors }"
              name="Graduation date"
              rules="required"
            >
              <flat-pickr
                v-model="graduationDate"
                class="form-control"
                :config="{ time_24hr: true }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            v-if="opintosuoritusoteExist"
            label="Credits"
            label-for="opintosuoritusoteDocument"
          >
            <validation-provider
              #default="{ errors }"
              name="Credits"
              rules="required"
            >
              <b-form-input
                id="credits"
                v-model.trim="credits"
                type="number"
                placeholder="Input Credits value"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <p class="mt-1 d-flex justify-content-between">
            <span>
              {{ $t('documentRequests.warningText.submit_documents')}}
            </span>
          </p>
        </b-form>
      </validation-observer>

      <template #modal-footer="{cancel}">
        <b-button
          variant="outline-secondary"
          @click="cancel()"
        >
          {{ $t('cancel') }}
        </b-button>
        <b-button
          variant="info"
          @click.prevent="handleSubmitDocumentSelected"
        >
          {{ $t('ok') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardHeader,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BLink,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import flatPickr from 'vue-flatpickr-component'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'

export default {
  components: {
    BButton,
    BCard,
    BCardHeader,
    BFormCheckbox,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BLink,

    flatPickr,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    documents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      required,
      indexDocumentRemoveItem: -1,
      selectedDocuments: [],
      loveGranted: '',
      graduationDate: '',
      credits: '',
    }
  },
  computed: {
    lääkehoitolupaExist() {
      return (
        this.selectedDocuments.includes('Lääkehoitolupa (teoria ja näytöt)')
        || this.selectedDocuments.includes('Love')
      )
    },
    diplomaExist() {
      return this.selectedDocuments.includes('Tutkintatodistus')
    },
    opintosuoritusoteExist() {
      return this.selectedDocuments.includes('Opintosuoritusote')
    },
    submitDocumentsShown() {
      return (
        this.documents
        && this.documents.filter(item => item.canEdit).length > 0
      )
    },
  },
  methods: {
    changeSubmittedDocument(document) {
      if (document.submitted) {
        this.selectedDocuments.push(document.name)
      } else {
        this.selectedDocuments = this.selectedDocuments.filter(
          item => item !== document.name,
        )
      }
    },
    handleSubmitDocumentSelected() {
      this.$refs.rules.validate().then(success => {
        if (success) {
          const newStateUser = {}
          const newStateDocuments = this.documents.map(
            ({ canEdit, ...document }) => document,
          )
          if (this.lääkehoitolupaExist) {
            newStateUser.loveGranted = new Date(this.loveGranted)
          }
          if (this.diplomaExist) {
            newStateUser.graduationDate = new Date(this.graduationDate)
          }
          if (this.opintosuoritusoteExist) {
            newStateUser.studies = { credits: +this.credits }
          }
          if (Object.keys(newStateUser).length > 0) {
            this.$emit('update-user-requested', newStateUser)
          }
          this.$emit('update-document-requested', newStateDocuments)
          this.$bvModal.hide('submit-modal')
        }
      })
    },
    removeDocument() {
      if (this.indexDocumentRemoveItem > -1) {
        const newStateDocuments = this.documents
          .filter((item, index) => this.indexDocumentRemoveItem !== index)
          .map(({ canEdit, ...document }) => document)
        this.$emit('update-document-requested', newStateDocuments)
      }
    },
  },
}
</script>

<style lang="scss">
@import "@hw-ui/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
div[role="tablist"] {
  flex-direction: column;
  .card {
    border-bottom: 0;
    ::v-deep .card-header {
      padding: 0.75rem 1.25rem 0.75rem 0.75rem;
      background-color: #fff;
      border: 1px solid rgba(34, 41, 47, 0.125);
      border-radius: 0.357rem;
      margin-bottom: 0.5rem;
    }
  }
}
</style>
